<template>
  <AuthVisualShell :character-src="characterSrc" character-alt="Personagem Zinnes" character-class="character-image--register">
    <main class="login-content auth-flow" aria-labelledby="register-title">
      <section v-if="step === 'form'" class="auth-step">
        <h1 id="register-title">REALIZAR CADASTRO</h1>
        <p class="auth-intro">Junte-se à nossa comunidade criativa.</p>
        <v-form ref="form" @submit.prevent="submitRegistration">
          <v-text-field v-model="name" label="Nome" :rules="nameRules" :error-messages="errors.name" required />
          <v-text-field v-model="nickname" label="Nickname" :rules="nicknameRules" :error-messages="errors.nickname" required />
          <v-text-field v-model.trim="email" label="E-mail" type="email" autocomplete="email" :rules="emailRules" :error-messages="errors.email" required />
          <v-text-field v-model="password" label="Senha" :type="showPassword ? 'text' : 'password'" autocomplete="new-password" :rules="passwordRules" :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" required @click:append="togglePassword('password')" />
          <v-text-field v-model="password_confirmation" label="Confirmar senha" :type="showPasswordConfirmation ? 'text' : 'password'" autocomplete="new-password" :rules="passwordConfirmationRules" :append-icon="showPasswordConfirmation ? 'mdi-eye-off' : 'mdi-eye'" required @click:append="togglePassword('confirmation')" />
          <v-btn class="btn-login auth-primary" color="orange" type="submit" :loading="isBusy" :disabled="isBusy">{{ isBusy ? 'Enviando...' : 'Cadastrar' }}</v-btn>
        </v-form>
        <p class="login">Já tem conta? <router-link to="/login">Efetuar login</router-link></p>
      </section>

      <section v-else-if="step === 'code'" class="auth-step">
        <h1 id="register-code-title" ref="codeHeading" tabindex="-1">Confirme seu e-mail</h1>
        <p class="auth-intro">Enviamos um código de 8 dígitos para confirmar que este e-mail é válido.</p>
        <p v-if="destination" class="masked-destination">Enviado para <strong>{{ destination }}</strong></p>
        <v-form @submit.prevent="submitCode">
          <v-text-field ref="codeInput" v-model="code" label="Código de confirmação" inputmode="numeric" autocomplete="one-time-code" maxlength="8" :rules="codeRules" :disabled="isBusy" />
          <v-btn class="btn-login auth-primary auth-code-submit" color="orange" type="submit" :loading="isBusy" :disabled="isBusy || code.length !== 8">{{ isBusy ? 'Confirmando...' : 'Confirmar e criar conta' }}</v-btn>
        </v-form>
        <p v-if="resendSeconds > 0" class="auth-status" role="status">Você poderá reenviar em {{ resendSeconds }}s.</p>
        <div class="auth-code-actions">
          <button class="auth-text-button" type="button" :disabled="isBusy || resendSeconds > 0" @click="resendCode">{{ isResending ? 'Enviando...' : 'Reenviar código' }}</button>
          <button class="auth-back-button" type="button" :disabled="isBusy" @click="backToForm">
            <v-icon small aria-hidden="true">mdi-arrow-left</v-icon>
            <span>Voltar ao cadastro</span>
          </button>
        </div>
      </section>

      <section v-else class="auth-step" aria-labelledby="register-success-title">
        <h1 id="register-success-title">Cadastro concluído</h1>
        <p class="auth-intro">Seu e-mail foi confirmado e sua conta foi criada com sucesso.</p>
        <v-btn class="btn-login auth-primary" color="orange" type="button" @click="$router.push('/login')">Ir para o login</v-btn>
      </section>

      <div class="auth-feedback" aria-atomic="true">
        <p v-if="errorMessage" class="auth-error" role="alert">{{ errorMessage }}</p>
        <p v-if="statusMessage" class="auth-status" role="status" aria-live="polite">{{ statusMessage }}</p>
      </div>
    </main>
  </AuthVisualShell>
</template>

<script>
import AuthVisualShell from '@/components/AuthVisualShell.vue';
import { registerUser, verifyRegistration, resendRegistrationCode } from '@/server/userServe';
import { nameRules, nicknameRules, emailRules, passwordRules, passwordConfirmationRules } from '@/utils/validationUtils';
import characterSrc from '@/assets/images/PersonZ.png';

const EXPIRATION_SECONDS = 600;

export default {
  name: 'RegisterPage',
  components: { AuthVisualShell },
  data() {
    return {
      step: 'form', name: '', nickname: '', email: '', password: '', password_confirmation: '',
      challenge: '', destination: '', code: '', expiresAt: 0, resendAt: 0, now: Date.now(), timerId: null,
      errorMessage: '', statusMessage: '', errors: {}, isBusy: false, isResending: false,
      showPassword: false, showPasswordConfirmation: false, nameRules, nicknameRules, emailRules, passwordRules,
      characterSrc,
    };
  },
  computed: {
    passwordConfirmationRules() { return passwordConfirmationRules(this.password); },
    codeRules() { return [value => /^\d{8}$/.test(value || '') || 'Informe o código de 8 dígitos.']; },
    resendSeconds() { return Math.max(0, Math.ceil((this.resendAt - this.now) / 1000)); },
  },
  created() { this.timerId = window.setInterval(this.refreshClock, 1000); },
  beforeDestroy() { window.clearInterval(this.timerId); },
  methods: {
    async submitRegistration() {
      if (this.isBusy || !this.$refs.form.validate()) { if (!this.isBusy) this.errorMessage = 'Por favor, corrija os erros no formulário.'; return; }
      this.isBusy = true; this.errorMessage = ''; this.statusMessage = 'Enviando confirmação para seu e-mail.';
      try {
        const response = await registerUser({ name: this.name, nickname: this.nickname.toLowerCase(), email: this.email.toLowerCase(), password: this.password, password_confirmation: this.password_confirmation });
        const data = response?.data || response;
        this.challenge = data.challenge_id || data.challenge;
        this.destination = data.destination || '';
        if (!this.challenge) throw new Error('Resposta inválida do cadastro.');
        this.expiresAt = Date.now() + Number(data.expires_in || EXPIRATION_SECONDS) * 1000;
        this.resendAt = Date.now() + Number(data.resend_after || 60) * 1000;
        this.password = ''; this.password_confirmation = ''; this.step = 'code'; this.statusMessage = 'Código enviado. Verifique sua caixa de entrada.';
        this.$nextTick(() => this.$refs.codeHeading?.focus());
      } catch (error) { this.setError(error); } finally { this.isBusy = false; }
    },
    async submitCode() {
      if (this.isBusy || !/^\d{8}$/.test(this.code)) { this.errorMessage = 'Informe o código de 8 dígitos.'; return; }
      if (this.expiresAt > 0 && Date.now() >= this.expiresAt) {
        this.step = 'form';
        this.code = '';
        this.challenge = '';
        this.errorMessage = 'Este código expirou. Solicite uma nova confirmação.';
        return;
      }
      this.isBusy = true; this.errorMessage = ''; this.statusMessage = 'Confirmando seu e-mail.';
      try { await verifyRegistration(this.challenge, this.code); this.code = ''; this.step = 'success'; this.statusMessage = ''; }
      catch (error) { this.setError(error); } finally { this.isBusy = false; }
    },
    async resendCode() {
      if (this.isBusy || this.resendSeconds > 0) return;
      this.isResending = true; this.errorMessage = '';
      try {
        const response = await resendRegistrationCode(this.challenge);
        const data = response?.data || response;
        this.resendAt = Date.now() + Number(data.resend_after || 60) * 1000;
        this.expiresAt = Date.now() + Number(data.expires_in || EXPIRATION_SECONDS) * 1000;
        this.code = ''; this.statusMessage = 'Novo código enviado.';
      } catch (error) { this.setError(error); } finally { this.isResending = false; }
    },
    backToForm() { this.step = 'form'; this.code = ''; this.errorMessage = ''; this.statusMessage = ''; },
    refreshClock() {
      this.now = Date.now();
      if (this.step === 'code' && this.expiresAt > 0 && this.now >= this.expiresAt) {
        this.step = 'form';
        this.code = '';
        this.challenge = '';
        this.errorMessage = 'Este código expirou. Solicite uma nova confirmação.';
        this.statusMessage = '';
      }
    },
    togglePassword(type) { if (type === 'password') this.showPassword = !this.showPassword; else this.showPasswordConfirmation = !this.showPasswordConfirmation; },
    setError(error) {
      this.statusMessage = '';
      this.errors = error?.errors || {};
      this.errorMessage = error?.message || 'Não foi possível concluir o cadastro. Tente novamente.';
    },
  },
};
</script>

<style scoped>
.auth-flow { width: 100%; }
.auth-feedback { min-height: 32px; }
.auth-error { color: #a51d1d; }
.auth-status { color: #665f55; }
.login { text-align: start; }
.auth-code-submit {
  display: block;
  width: min(100%, 420px);
  margin: 18px auto 14px;
  white-space: normal;
}
.auth-code-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.auth-code-actions button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 2px 4px;
  color: orange;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.auth-code-actions button:disabled {
  color: #9e9e9e;
}
</style>
